/**
 * Merge-field filling for clinic print letters (PostGrid-designed HTML printed in the office).
 * See docs/CUSTOM_PRINT_LETTER.md for the field list.
 */

export type PrintLetterValues = Record<string, string | null | undefined>;

/** Canonical field names the patient page fills. */
export const PRINT_LETTER_FIELDS = [
  'firstName',
  'lastName',
  'patientName',
  'patientNote',
  'practiceName',
  'doctorName',
  'date',
  'scheduleDate1',
  'scheduleDate2',
  'scheduleDate3',
  'scheduleDate4',
  'patientAddress',
  'clinicPhone'
] as const;

export type PrintLetterField = (typeof PRINT_LETTER_FIELDS)[number];

/** Other spellings (lowercase) of a canonical field. PostGrid contact fields use `to.`. */
const FIELD_ALIASES: Record<string, PrintLetterField> = {
  'to.firstname': 'firstName',
  'to.lastname': 'lastName',
  patientnotes: 'patientNote',
  'to.description': 'patientNote',
  clinicname: 'practiceName',
  'from.companyname': 'practiceName',
  currentdate: 'date',
  nextscheduledate: 'scheduleDate1',
  'to.address': 'patientAddress'
};

const FIELD_PATTERN = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;
const LEFTOVER_PATTERN = /\{\{[^{}]*\}\}/g;

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Escaped value with line breaks kept as `<br>`. */
export function printLetterValueHtml(value: string | null | undefined): string {
  const text = (value ?? '').replace(/\r\n?/g, '\n').trim();
  return escapeHtml(text).replace(/\n/g, '<br>');
}

function indexValues(values: PrintLetterValues): Map<string, string> {
  const index = new Map<string, string>();
  Object.keys(values).forEach((key) => {
    const value = values[key];
    if (typeof value === 'string') index.set(key.toLowerCase(), value);
  });
  return index;
}

function resolveField(name: string, index: Map<string, string>): string {
  const lower = name.toLowerCase();
  const direct = index.get(lower);
  if (direct !== undefined) return direct;
  const alias = FIELD_ALIASES[lower];
  if (alias) return index.get(alias.toLowerCase()) ?? '';
  // `to.foo` falls back to `foo` (PostGrid contact fields).
  if (lower.startsWith('to.')) return index.get(lower.slice(3)) ?? '';
  return '';
}

/**
 * Replace `{{ field }}` merge fields with HTML-escaped values. Unknown or empty fields become an
 * empty string, and no `{{…}}` is ever left in the result.
 */
export function fillPrintLetter(html: string, values: PrintLetterValues): string {
  const index = indexValues(values);
  return html
    .replace(FIELD_PATTERN, (_match, name: string) =>
      printLetterValueHtml(resolveField(name, index))
    )
    .replace(LEFTOVER_PATTERN, '');
}

/** Distinct merge-field names used in a template, as written. */
export function printLetterFieldsIn(html: string): string[] {
  const names = new Set<string>();
  html.replace(FIELD_PATTERN, (match, name: string) => {
    names.add(name);
    return match;
  });
  return [...names];
}

/** Which of scheduleDate1..4 a template uses (1-based). */
export function scheduleDateSlotsIn(html: string): number[] {
  const slots = new Set<number>();
  printLetterFieldsIn(html).forEach((name) => {
    const lower = name.toLowerCase();
    const canonical = FIELD_ALIASES[lower] ?? (lower.startsWith('to.') ? lower.slice(3) : lower);
    const match = canonical.toLowerCase().match(/^scheduledate([1-4])$/);
    if (match) slots.add(Number(match[1]));
  });
  return [...slots].sort((a, b) => a - b);
}

export interface PrintLetterPatient {
  givenName?: string | null;
  familyName?: string | null;
  patientPrintNote?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  } | null;
  clinic?: {
    name?: string | null;
    friendlyName?: string | null;
    phoneNumber?: string | null;
  } | null;
  practitioner?: {
    given?: string | null;
    family?: string | null;
  } | null;
}

function clean(value?: string | null): string {
  return (value ?? '').trim();
}

/** MM-DD-YYYY, matching the mailed letters. */
export function formatPrintLetterDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}-${day}-${date.getFullYear()}`;
}

/** Mailing address, one line per row: street, unit, "City, ST 12345". */
export function formatPrintLetterAddress(address: PrintLetterPatient['address']): string {
  const cityState = [clean(address?.city), clean(address?.state)].filter(Boolean).join(', ');
  const lastLine = [cityState, clean(address?.postalCode)].filter(Boolean).join(' ');
  return [clean(address?.line1), clean(address?.line2), lastLine].filter(Boolean).join('\n');
}

export interface BuildPrintLetterValuesInput {
  patient: PrintLetterPatient;
  /** Up to four MM-DD-YYYY dates, in order. */
  scheduleDates: string[];
  /** Note for this print. Defaults to the saved patient print note. */
  note?: string;
  today?: Date;
}

export function buildPrintLetterValues({
  patient,
  scheduleDates,
  note,
  today = new Date()
}: BuildPrintLetterValuesInput): Record<PrintLetterField, string> {
  const firstName = clean(patient.givenName);
  const lastName = clean(patient.familyName);
  const doctorName = [clean(patient.practitioner?.given), clean(patient.practitioner?.family)]
    .filter(Boolean)
    .join(' ');
  return {
    firstName,
    lastName,
    patientName: [firstName, lastName].filter(Boolean).join(' '),
    patientNote: note ?? patient.patientPrintNote ?? '',
    practiceName: clean(patient.clinic?.friendlyName) || clean(patient.clinic?.name),
    doctorName,
    date: formatPrintLetterDate(today),
    scheduleDate1: clean(scheduleDates[0]),
    scheduleDate2: clean(scheduleDates[1]),
    scheduleDate3: clean(scheduleDates[2]),
    scheduleDate4: clean(scheduleDates[3]),
    patientAddress: formatPrintLetterAddress(patient.address),
    clinicPhone: clean(patient.clinic?.phoneNumber)
  };
}
